import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './LeakDetectedModal.css';

interface Props {
  isOpen: boolean;
  onRestart: () => void;
  onCancel: () => void;
}

const LeakDetectedModal: React.FC<Props> = ({ isOpen, onRestart, onCancel }) => {
  const { t } = useTranslation('learning');
  const restartBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    requestAnimationFrame(() => restartBtnRef.current?.focus());
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <div
      className="leak-overlay"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="leak-title"
    >
      <div className="leak-modal" onClick={(e) => e.stopPropagation()}>
        <div className="leak-icon" aria-hidden="true">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
            <path d="M21 19a2 2 0 0 1-2 2h-1v-7h3v5z" />
            <path d="M3 19a2 2 0 0 0 2 2h1v-7H3v5z" />
            <line x1="2" y1="2" x2="22" y2="22" />
          </svg>
        </div>
        <h2 id="leak-title" className="leak-title">
          {t('shadowing.leakDetected.title')}
        </h2>
        <p className="leak-body">{t('shadowing.leakDetected.body')}</p>
        <div className="leak-actions">
          <button
            ref={restartBtnRef}
            type="button"
            className="leak-btn leak-btn-primary"
            onClick={onRestart}
          >
            {t('shadowing.leakDetected.restart')}
          </button>
          <button
            type="button"
            className="leak-btn leak-btn-secondary"
            onClick={onCancel}
          >
            {t('shadowing.leakDetected.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeakDetectedModal;
